import { requestHandler as AxiosRequestHandler } from 'api/axios/helpers';
import { useGlobal } from './useGlobal';
import { AxiosError, HttpStatusCode } from 'axios';
import { useRouter } from 'next/router';
import { Button, Typography } from 'antd/lib';
import { ExceptionDetail, SessionDTO, StatusResponse } from 'api/api_code';
import { useLoading } from './useLoading';
import {
	getFromSessionStorage,
	setToSessionStorage,
} from 'helpers/storageUtils';
import { IconType } from 'components/UI/Modals/NotificationModal';
import useTranslation from 'next-translate/useTranslation';
import {
	cameraConfigSelector,
	isProBroadcastSelector,
	useCameraStore,
} from 'stores/camera.store';
import { getSportName } from 'helpers/utils';
import Trans from 'next-translate/Trans';
import Link from 'antd/lib/typography/Link';
import { AxiosManager } from 'api/axios/axios';
import { X_WIZARD_SESSION_TOKEN } from 'api/axios/constants';
import { DevicesEndpoints } from 'api/axios/axiosAPIGroups';
import {
	handleRequestRetry,
	WIZARD_SESSION_RETRY_TIME,
} from 'helpers/handleRequestRetry';
import { getOrCreateWizardSessionRequestId } from 'helpers/requestIdUtils';

export function useHTTPRequestHandler() {
	const router = useRouter();
	const { setOpenNotification } = useGlobal();
	const deviceId = (router.query['device_uuid'] as string) ?? 'noToken';
	const cameraConfig = useCameraStore(cameraConfigSelector);
	const fieldId =
		getFromSessionStorage('field_uuid') ?? cameraConfig?.fields?.[0]?.uuid;
	const { setLoading } = useLoading();
	const { t } = useTranslation('common');
	const isProBroadcast = useCameraStore(isProBroadcastSelector);

	const cancelForceSession = () => {
		setOpenNotification({ open: false });
		if (router.pathname !== '/camera/[device_uuid]/details') {
			router.push({
				pathname: '/camera/[device_uuid]/details',
				query: { device_uuid: deviceId },
			});
		}
	};

	const forceStartSession = async ({
		device_uuid,
		field_uuid,
		callback,
		reload = true,
	}: {
		device_uuid: string;
		field_uuid: string;
		callback?: CallableFunction;
		reload?: boolean;
	}) => {
		const requestId = getOrCreateWizardSessionRequestId();
		requestHandler({
			requestPromise:
				DevicesEndpoints.startWizardSessionDevicesDeviceUuidSessionPost(
					device_uuid,
					requestId,
					field_uuid,
					true
				),
			onOkCallback: (session: SessionDTO | StatusResponse) => {
				if ((session as StatusResponse).status === 'in_progress') {
					handleRequestRetry({
						callback: () =>
							forceStartSession({ device_uuid, field_uuid, callback }),
						time: WIZARD_SESSION_RETRY_TIME,
					});
					return;
				}
				if ((session as StatusResponse).status === 'failed') {
					throw new Error('wizard session failed');
				}
				setToSessionStorage('session_token', session['token']);
				AxiosManager.getInstance().addCommonHeader(
					X_WIZARD_SESSION_TOKEN,
					session['token']
				);
				if (callback) {
					return callback();
				}
				reload && router.reload();
			},
		});
	};

	const checkErrors = (error: AxiosError, onKoCallback?: CallableFunction) => {
		const internalCode = (error.response?.data as ExceptionDetail)?.code;
		const internalMsg = (error.response?.data as ExceptionDetail)?.message;
		// Session errors
		let title = '',
			body = <></>,
			onOk = () => {
				setOpenNotification({ open: false });
				router.push('/');
			};
		if (
			error.response?.status === HttpStatusCode.BadRequest &&
			(internalCode === 1009 || internalCode === 1010)
		) {
			title = t('sessionModal.title');
			body = (
				<div>
					<div>{internalMsg}</div>
					<div>{t('sessionModal.request')}</div>
				</div>
			);

			return {
				title,
				body,
				onOk: () => null,
				onCancel: cancelForceSession,
				footerBtns: [
					<Button
						key="cancel-button"
						aria-label="cancel close session"
						onClick={cancelForceSession}
					>
						{t('sessionModal.cancel')}
					</Button>,
					<Button
						key="confirm-button"
						aria-label="confirm close session"
						type="primary"
						onClick={() => {
							void forceStartSession({
								device_uuid: deviceId,
								field_uuid: fieldId,
								callback: onKoCallback,
							});
							setOpenNotification({ open: false });
						}}
					>
						{t('sessionModal.confirm')}
					</Button>,
				],
			};
		}
		if (internalCode === 1102) {
			const splittedUrl = error?.response?.config?.url
				? error.response.config.url.split('/')
				: [];
			const sport_uuid = splittedUrl[splittedUrl.length - 2];
			title = t('modal.deleteSportWithPreset.title');
			body = (
				<Typography.Paragraph>
					{t('modal.deleteSportWithPreset.description', {
						sport: getSportName(sport_uuid),
					})}
				</Typography.Paragraph>
			);
			onOk = () => {
				setOpenNotification({ open: false });
			};
			const footerBtns = [
				<Button
					key="modal-footer-btn"
					onClick={onOk}
					type="ghost"
					style={{ width: 'max-content' }}
				>
					{t('ok')}
				</Button>,
			];
			const titleIconType = IconType.KO;
			return {
				titleIconType,
				title,
				body,
				onOk,
				onCancel: onOk,
				footerBtns,
			};
		}
		if (internalCode === 1014) {
			title = t('recordingModal.title');
			body = (
				<Typography.Paragraph>
					{t('recordingModal.explanation')}
				</Typography.Paragraph>
			);
			onOk = () => {
				setOpenNotification({ open: false });
				router.push('/');
			};
			const footerBtns = [
				<Button
					key="modal-footer-btn"
					onClick={onOk}
					type="ghost"
					style={{ width: 'max-content' }}
				>
					{t('ok')}
				</Button>,
			];
			const titleIconType = IconType.RECORDING;
			return {
				titleIconType,
				title,
				body,
				onOk,
				onCancel: onOk,
				footerBtns,
			};
		}
		return {
			onOk,
			closable: false,
			title: t('modal.generic.title'),
			body: (
				<div>
					<Trans
						i18nKey="common:modal.generic.description"
						components={[
							<Link key="mail-support" href="mailto:support@automatic.tv">
								{t('common:modal.generic.supportEmail')}
							</Link>,
						]}
					/>
				</div>
			),
		};
	};

	const defaultErrorCallback = (error, onKoCallback?: CallableFunction) => {
		/**
		 * Error code 1011: doesn't have any session active
		 * do not show any notification, redirect to camera list page
		 */
		if (
			error instanceof AxiosError &&
			(error.response?.data as ExceptionDetail)?.code === 1011 &&
			error.response?.status === 400
		) {
			if (router.pathname !== '/' && !isProBroadcast) {
				sessionStorage.removeItem('session_token');
				void forceStartSession({
					device_uuid: deviceId,
					field_uuid: fieldId,
				});
				return;
			}
		}
		/**
		 * Response status 401: user not logged
		 * do not show any notification, redirect to sso login
		 */
		if (error instanceof AxiosError && error.response?.status === 401) {
			return;
		}
		let notificationProps = {
			onOk: () => {
				setOpenNotification({ open: false });
				router.push('/');
			},
			title: t('modal.generic.title'),
			body: (
				<div>
					<Trans
						i18nKey="common:modal.generic.description"
						components={[
							<Link key="mail-support" href="mailto:support@automatic.tv">
								{t('common:modal.generic.supportEmail')}
							</Link>,
						]}
					/>
				</div>
			),
		};
		if (error instanceof AxiosError) {
			notificationProps = checkErrors(error, onKoCallback);
		}

		setOpenNotification({
			open: true,
			...notificationProps,
		});
	};
	const handleKoCallback = (
		error,
		onKo: {
			onKoCallback: CallableFunction;
			overrideCallback?: boolean | ((error: AxiosError) => boolean);
		}
	) => {
		setLoading(false);

		const shouldOverride =
			typeof onKo?.overrideCallback === 'function'
				? onKo.overrideCallback(error)
				: onKo?.overrideCallback;

		if (shouldOverride) {
			onKo.onKoCallback(error);
		} else {
			defaultErrorCallback(error, onKo?.onKoCallback);
		}
	};

	const requestHandler = ({
		requestPromise,
		onOkCallback,
		onKo,
		autoLoading = true,
	}: {
		requestPromise: Promise<unknown>;
		onOkCallback?: CallableFunction;
		onKo?: {
			onKoCallback: CallableFunction;
			overrideCallback?: boolean | ((error: AxiosError) => boolean);
		};
		autoLoading?: boolean;
	}) => {
		if (autoLoading) {
			setLoading(true);
		}
		AxiosRequestHandler.call(
			this,
			requestPromise,
			(requestData, pendingRequests: number) => {
				if (pendingRequests === 0 && autoLoading) {
					setLoading(false);
				}
				onOkCallback?.(requestData);
			},
			(error) => handleKoCallback(error, onKo)
		);
	};

	return {
		requestHandler,
		cancelForceSession,
		forceStartSession,
	};
}
